const normalizeId = (value) => (value == null ? null : String(value));

const activityTransientKeys = new Set(['__liveNew', '__liveNewAt']);

const activityItemSnapshot = (item = {}) => JSON.stringify(
  Object.fromEntries(
    Object.entries(item).filter(([key]) => !activityTransientKeys.has(key)),
  ),
);

const reconcileActivityItem = (previous, incoming) => {
  if (!previous) return incoming;

  const candidate = {
    ...incoming,
    ...(previous.__liveNew === true ? { __liveNew: true } : {}),
    ...(previous.__liveNewAt != null ? { __liveNewAt: previous.__liveNewAt } : {}),
  };

  return activityItemSnapshot(previous) === activityItemSnapshot(candidate)
    ? previous
    : candidate;
};

const isLiveActivityArrival = (item = {}) => {
  if (item.live_scope_added === true || Number(item.live_scope_added) === 1) return true;
  if (item.live_created === true || Number(item.live_created) === 1) return true;
  if (item.live_created === false || Number(item.live_created) === 0) return false;

  // Compatibility with a backend that predates the explicit live_created
  // field: edits carry updated_at, while untouched inserts generally do not.
  return !item.updated_at;
};

export const mergeActivityFeedPage = (
  currentItems = [],
  incomingItems = [],
  { append = false } = {},
) => {
  if (append) {
    const seen = new Set(
      currentItems
        .map((item) => normalizeId(item?.id))
        .filter((id) => id !== null),
    );
    const additions = incomingItems.filter((item) => {
      const id = normalizeId(item?.id);
      if (id === null) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    return additions.length === 0 ? currentItems : [...currentItems, ...additions];
  }

  const currentById = new Map(
    currentItems
      .map((item) => [normalizeId(item?.id), item])
      .filter(([id]) => id !== null),
  );
  const seen = new Set();
  const nextItems = [];

  incomingItems.forEach((item) => {
    const id = normalizeId(item?.id);
    if (id === null) {
      nextItems.push(item);
      return;
    }
    if (seen.has(id)) return;
    seen.add(id);
    nextItems.push(reconcileActivityItem(currentById.get(id), item));
  });

  const unchanged = nextItems.length === currentItems.length
    && nextItems.every((item, index) => item === currentItems[index]);
  return unchanged ? currentItems : nextItems;
};

export const canCommitActivityLiveSnapshot = (
  requestVersion,
  currentRequestVersion,
  dataVersion,
  currentDataVersion,
) => (
  requestVersion === currentRequestVersion
  && dataVersion === currentDataVersion
);

export const applyActivityScopeChanges = (
  changes = [],
  scopeChanges = [],
) => {
  const finalScopeChangeById = new Map();
  scopeChanges.forEach((scopeChange) => {
    const id = normalizeId(scopeChange?.id);
    const direction = scopeChange?.change;
    if (id === null || (direction !== 'added' && direction !== 'removed')) return;
    finalScopeChangeById.set(id, direction);
  });

  const emitted = new Set();
  const reconciled = changes.map((change) => {
    const id = normalizeId(change?.id);
    if (id === null) return change;
    emitted.add(id);

    const direction = finalScopeChangeById.get(id);
    if (direction === 'removed') {
      // A source-case reader only needs the identity of the row it already
      // loaded. Never retain fields fetched for some other scope.
      return { id: change.id, deleted: 1, live_scope_removed: true };
    }
    if (direction === 'added') {
      return { ...change, live_scope_added: true };
    }
    return change;
  });

  finalScopeChangeById.forEach((direction, id) => {
    if (direction !== 'removed' || emitted.has(id)) return;
    reconciled.push({ id, deleted: 1, live_scope_removed: true });
  });

  return reconciled;
};

export const mergeActivityFeedChanges = (
  currentItems = [],
  changes = [],
  { matches = () => true, now = Date.now(), prependNew = true } = {},
) => {
  if (!Array.isArray(changes) || changes.length === 0) {
    return {
      items: currentItems,
      newIds: [],
      countDelta: 0,
      changed: false,
    };
  }

  const currentById = new Map();
  currentItems.forEach((item) => {
    const id = normalizeId(item?.id);
    if (id !== null) currentById.set(id, item);
  });

  const nextById = new Map(currentById);
  const newIds = [];
  let countDelta = 0;
  let changed = false;

  changes.forEach((change) => {
    const id = normalizeId(change?.id);
    if (id === null) return;

    const previous = nextById.get(id);
    const previouslyVisible = Boolean(previous);
    const deleted = Number(change?.deleted) === 1;
    const remainsVisible = !deleted && matches(change, nextById);

    if (!remainsVisible) {
      if (previouslyVisible) {
        nextById.delete(id);
        countDelta -= 1;
        changed = true;
      }
      return;
    }

    if (!previouslyVisible) {
      // A live delta also contains edits to rows that may simply be beyond the
      // pages loaded in this browser. Do not promote those old rows to the top
      // of the feed or label them as newly created.
      if (!isLiveActivityArrival(change)) return;

      nextById.set(id, {
        ...change,
        __liveNew: true,
        __liveNewAt: now,
      });
      newIds.push(id);
      countDelta += 1;
      changed = true;
      return;
    }

    const candidate = {
      ...previous,
      ...change,
      __liveNew: previous.__liveNew === true,
      __liveNewAt: previous.__liveNewAt,
    };
    const reconciled = activityItemSnapshot(previous) === activityItemSnapshot(candidate)
      ? previous
      : candidate;
    nextById.set(id, reconciled);
    if (reconciled !== previous) changed = true;
  });

  if (!changed) {
    return {
      items: currentItems,
      newIds,
      countDelta,
      changed: false,
    };
  }

  const nextItems = [];
  const emitted = new Set();

  currentItems.forEach((item) => {
    const id = normalizeId(item?.id);
    if (id === null) {
      nextItems.push(item);
      return;
    }
    const nextItem = nextById.get(id);
    if (!nextItem) return;
    nextItems.push(nextItem);
    emitted.add(id);
  });

  const newItems = [];
  newIds.forEach((id) => {
    if (emitted.has(id)) return;
    const item = nextById.get(id);
    if (item) newItems.push(item);
  });

  return {
    items: prependNew ? [...newItems, ...nextItems] : [...nextItems, ...newItems],
    newIds,
    countDelta,
    changed: true,
  };
};

const sharedItemIdentity = (item = {}) => String(item.key || item.name || '');

const sharedItemSnapshot = (item = {}) => JSON.stringify({
  key: item.key || '',
  name: item.name || '',
  size: Number(item.size || 0),
  items: Number(item.items || 0),
  items_complete: item.items_complete !== false,
  last_modified: item.last_modified || null,
  url: item.url || '',
  open_url: item.open_url || '',
  download_url: item.download_url || '',
});

const reconcileSharedCollection = (current = [], incoming = [], { preserveIncompleteCounts = false } = {}) => {
  const currentByKey = new Map(
    current.map((item) => [sharedItemIdentity(item), item]),
  );

  return incoming.map((item) => {
    const previous = currentByKey.get(sharedItemIdentity(item));
    if (!previous) return item;
    const candidate = preserveIncompleteCounts && item?.items_complete === false
      ? {
        ...item,
        items: previous.items,
        items_complete: previous.items_complete,
      }
      : item;
    return sharedItemSnapshot(previous) === sharedItemSnapshot(candidate) ? previous : candidate;
  });
};

export const reconcileSharedViewerData = (current, incoming) => {
  if (!current || !incoming || !incoming.success || !incoming.is_folder) {
    return incoming;
  }

  return {
    ...current,
    ...incoming,
    folders: reconcileSharedCollection(current.folders, incoming.folders, {
      preserveIncompleteCounts: true,
    }),
    files: reconcileSharedCollection(current.files, incoming.files),
  };
};

export const sharedViewerRowIds = (shareData, currentPath = '') => {
  const pathPrefix = currentPath || '';
  return new Set([
    ...((shareData?.folders || []).map((folder) => (
      `folder:${pathPrefix}/${folder.name}`.replace(/\/+/g, '/')
    ))),
    ...((shareData?.files || []).map((file) => (
      `file:${pathPrefix}/${file.name}`.replace(/\/+/g, '/')
    ))),
  ]);
};
